/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import { SxProps } from "@mui/material";
import { adddevicesLabelColumns, devicesLabelColumns } from "../../../Shared/Table/Columns";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { useDevices } from "./hooks";
import { setResetAddDeviceFilters, setResetFilters } from "../../../../store/filters/devices/Label/LabelDeviceFilterReducer";
import { selectProfilePermissionsSelector } from "../../../../store/api/auth/selectors";
import ProfilePermissions from "../../../../utils/ProfilePermissions";
import { useSelector } from "react-redux";
import { IFilter } from "../../../Shared/Filter/types";
import AssociationControl from "../../../Shared/Association/AssociationControl";
import { payloadKeyList } from "../../../Shared/Association/AssociationHelper";
import DeviceDrawer from "../../../Drawers/DeviceDrawer";

interface DevicesProps {
    onAssociationChanged?: () => void;
}

const Devices: React.FC<DevicesProps> = ({ onAssociationChanged }) => {
    const MAX_LABELS = 10;
    const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
    const canManageLabels = permissions?.hasOpCodeGroup("manage_label_management") ?? false;
    const {
        searchFilters,
        searchAddFilters,
        label
    } = useDevices();
    const hasValidLabel = !!label?.value && label.value !== "NONE";
    const isLabelSelected = hasValidLabel ? true : false;

    const removeDeviceFilters: IFilter = {
        searchTooltip: "",
        selectFilters: searchFilters
    };

    const addDeviceFilters: IFilter = {
        searchTooltip: "",
        selectFilters: searchAddFilters
    };

    const rowSelectableCondition = (params: any) => {
        return (params.row?.labelCount ?? 0) < MAX_LABELS;
    };

    const disabledActionButton = (params: any) => {
        return params.row?.labelCount >= MAX_LABELS;
    };

    const setRowClassName = (params: any) => {
        return params.row?.labelCount >= MAX_LABELS ? "label-limit-row" : ""
    };

    const sx: SxProps = {
        "& .label-limit-row": {
            backgroundColor: "var(--border)",
            color: "var(--muted-foreground)",
        }
    }

    return (
        <>
        <div className="flex-1 ml-3 mr-3">
            <AssociationControl
                entity={label}
                parentEntityName={"label"}
                childEntityName={"Device"}
                associationPermission={canManageLabels}
                listFilters={removeDeviceFilters}
                listColumns={devicesLabelColumns}
                listSorting={"name, asc"}
                listResetFilterHandler={setResetFilters}
                listCheckboxSelection={true}
                listDisableColumnMenu={true}
                listDisableRowSelectionOnClick={true}
                listMutationKey={"removeLabelFromDevice"}
                listMutationPayloadKey={payloadKeyList.removeLabelFromDevice}
                urlListParameterKey={"getLabelMapProfile"}
                loadListDataKey={"getLabelMapProfile"}
                listDialogOpener={DIALOGOPENFOR.ADDLABELDEVICE}
                addModelHeaderName={"Add device"}
                visibleAdd={true}
                disableAddButton={!isLabelSelected}
                addButtonName={"Add Device"}
                showRemoveAction={true}
                confirmRemoveDialogOpener={DIALOGOPENFOR.REMOVELABELDEVICES}
                removeConfirmationMessage={"Are you sure you want to remove the selected device from label:"}
                addFilters={addDeviceFilters}
                addColumns={adddevicesLabelColumns}
                addSorting={"name, asc"}
                addResetFilterHandler={setResetAddDeviceFilters}
                showAddAction={false}
                showAddAll={true}
                addCheckboxSelection={true}
                addDisableColumnMenu={true}
                addDisableRowSelectionOnClick={true}
                addMutationKey={"addLabelToDevice"}
                addMutationPayloadKey={payloadKeyList.addLabelToDevice}
                urlAddParameterKey={"getLabelUnmapProfile"}
                loadAddDataKey={"getLabelUnmapProfile"}
                loadAddAllDataKey={"getLabelUnmapAllProfile"}
                addShowInfoMessage={true}
                addInfoMessage={`Devices with ${MAX_LABELS} labels will be excluded from the 'Add All' operation.`}
                addRowSelectable={rowSelectableCondition}
                addSetRowClass={setRowClassName}
                disabledAddActionButton={disabledActionButton}
                sxAdd={sx}
                onAssociationChanged={onAssociationChanged}
                listHeight={"460px"}
                listDrawerItem={DeviceDrawer}
            />
        </div>
        </>
    );
};

export default Devices;